import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { activeLayoutAtom, selectedCompanyIdAtom } from '../state';
import { DropTarget, DropZone, TearsheetPanelType } from '../types';
import { defaultLayout } from '../utils/defaultLayout';
import {
  addPanelToLayout,
  getUsedPanelTypes,
  movePanel as movePanelUtil,
  removeCell,
  updateCellSizes,
} from '../utils/layoutGrid';

export function useLayoutActions() {
  const [layout, setLayout] = useAtom(activeLayoutAtom);
  const selectedCompanyId = useAtomValue(selectedCompanyIdAtom);
  const usedPanels = useMemo(() => getUsedPanelTypes(layout), [layout]);

  const addPanel = useCallback(
    (panelType: TearsheetPanelType, dropTarget?: DropTarget) => {
      const zone = dropTarget?.zone ?? 'bottom';
      const targetCellId = dropTarget?.type === 'panel' ? dropTarget.cellId : undefined;

      const newLayout = addPanelToLayout(layout, panelType, targetCellId, zone);
      setLayout(newLayout);
    },
    [layout, setLayout]
  );

  const removePanel = useCallback(
    (cellId: string) => {
      const newLayout = removeCell(layout, cellId);
      setLayout(newLayout);
    },
    [layout, setLayout]
  );

  const movePanel = useCallback(
    (sourceCellId: string, targetCellId: string, zone: DropZone) => {
      const newLayout = movePanelUtil(layout, sourceCellId, targetCellId, zone);
      setLayout(newLayout);
    },
    [layout, setLayout]
  );

  const updateSizes = useCallback(
    (boxCellId: string, newSizes: number[]) => {
      const newLayout = updateCellSizes(layout, boxCellId, newSizes);
      setLayout(newLayout);
    },
    [layout, setLayout]
  );

  const resetLayout = useCallback(() => {
    if (selectedCompanyId !== null) {
      setLayout(null);
    } else {
      setLayout(defaultLayout);
    }
  }, [setLayout, selectedCompanyId]);

  return {
    layout,
    usedPanels,
    addPanel,
    removePanel,
    movePanel,
    updateSizes,
    resetLayout,
  };
}
